import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listenToUploadBatchStatus,
  UPLOAD_BATCH_STATUS_VALUES,
  type FirebaseUploadBatchData,
  type UploadBatchStatus,
} from '@/services/firebase';

export interface UploadBatchState {
  batchId: string;
  documentId: string;
  status: UploadBatchStatus;
  totalCount: number;
  processedCount: number;
  error?: string;
}

export const useUploadBatchListener = (): {
  batches: Record<string, UploadBatchState>;
  startListening: (
    documentId: string,
    onComplete?: (data: FirebaseUploadBatchData) => void,
    onError?: (error: string) => void
  ) => void;
  stopListening: (documentId: string) => void;
} => {
  const [batches, setBatches] = useState<Record<string, UploadBatchState>>({});
  const activeListenersRef = useRef<Record<string, () => void>>({});
  const previousStatusRef = useRef<Record<string, string>>({});

  const stopListening = useCallback((documentId: string): void => {
    if (activeListenersRef.current[documentId]) {
      activeListenersRef.current[documentId]();
      delete activeListenersRef.current[documentId];
    }
    delete previousStatusRef.current[documentId];
  }, []);

  const startListening = useCallback(
    (
      documentId: string,
      onComplete?: (data: FirebaseUploadBatchData) => void,
      onError?: (error: string) => void
    ): void => {
      if (activeListenersRef.current[documentId]) {
        return;
      }

      const unsubscribe = listenToUploadBatchStatus(
        documentId,
        async (data: FirebaseUploadBatchData) => {
          const docId = data.documentId || documentId;
          const previousStatus = previousStatusRef.current[docId] || '';
          const currentStatus = data.status;

          if (previousStatus === currentStatus) {
            return;
          }

          previousStatusRef.current[docId] = currentStatus;

          // Update batch state
          setBatches((prev) => ({
            ...prev,
            [docId]: {
              batchId: data.batchId,
              documentId: docId,
              status: data.status,
              totalCount: data.totalCount,
              processedCount: data.processedCount || 0,
              error: data.error,
            },
          }));

          // Handle completion
          if (currentStatus === UPLOAD_BATCH_STATUS_VALUES.COMPLETED) {
            onComplete?.(data);
            stopListening(docId);
          }

          // Handle failure
          if (currentStatus === UPLOAD_BATCH_STATUS_VALUES.FAILED) {
            console.error(`Upload batch failed ${docId}:`, data.error);
            onError?.(data.error || 'Upload failed');
            stopListening(docId);
          }
        },
        (error) => {
          console.error('Listener error:', error);
          onError?.('Firebase connection error');
          stopListening(documentId);
        }
      );

      activeListenersRef.current[documentId] = unsubscribe;
    },
    [stopListening]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.values(activeListenersRef.current).forEach((unsubscribe) =>
        unsubscribe()
      );
      activeListenersRef.current = {};
    };
  }, []);

  return {
    batches,
    startListening,
    stopListening,
  };
};
